// This file is a near-copy of src/common/barcode_reader.ts of
// @ericblade/quagga2, under the MIT License.

import { CodeScannerStandard } from "common/code-scanner-standards";

// Copyright (c) 2014 Christoph Oberhofer, (c) 2019 Eric Blade and all other
// contributors
export enum BarcodeDirection {
    Forward = 1,
    Reverse = -1,
}

export type BarcodeReaderType = string;
export type BarcodeFormat = CodeScannerStandard;

export interface BarcodeReaderConfig {
    normalizeBarSpaceWidth?: boolean;
    supplements?: Array<BarcodeReaderType>;
}

export interface BarcodeCorrection {
    bar: number;
    space: number;
}

export interface BarcodePosition {
    start: number;
    startCounter?: number;
    end: number;
    endCounter?: number;
    error?: number;
}

export interface BarcodeInfo extends BarcodePosition {
    code: number;
    correction?: BarcodeCorrection;
}

export interface Barcode {
    code: string;
    codeset?: number;
    correction?: BarcodeCorrection;
    decodedCodes?: Array<string | BarcodeInfo | BarcodePosition>;
    direction?: BarcodeDirection;
    end: number;
    endInfo?: BarcodePosition;
    format: BarcodeFormat;
    start: number;
    startInfo: BarcodePosition;
    supplement?: Barcode;
}

export abstract class BarcodeReader {
    _row: Array<number> = [];
    supplements: Array<BarcodeReader> = [];
    SINGLE_CODE_ERROR = 0;
    FORMAT: BarcodeFormat = CodeScannerStandard.Unknown;
    CONFIG_KEYS: BarcodeReaderConfig = {};
    // TODO: should add ALPHABETH_STRING, ALPHABET, CHARACTER_ENCODINGS to base class, if they
    // are useful in most readers.

    abstract _decode(row?: Array<number>, start?: BarcodePosition | number): Barcode | null;

    static get Exception() {
        return {
            StartNotFoundException: "Start-Info was not found!",
            CodeNotFoundException: "Code could not be found!",
            PatternNotFoundException: "Pattern could not be found!",
        };
    }

    constructor(public config: BarcodeReaderConfig, supplements?: Array<BarcodeReader>) {
        this._row = [];
        if (supplements) {
            this.supplements = supplements;
        }
        return this;
    }

    _nextUnset(line: ReadonlyArray<number>, start: number = 0): number {
        for (let i = start; i < line.length; i++) {
            if (!line[i]) return i;
        }
        return line.length;
    }

    _matchPattern(counter: ReadonlyArray<number>, code: ReadonlyArray<number>, maxSingleError?: number): number {
        let error = 0;
        let singleError = 0;
        let sum = 0;
        let modulo = 0;
        let barWidth = 0;
        let count = 0;
        let scaled = 0;

        maxSingleError = maxSingleError ?? this.SINGLE_CODE_ERROR ?? 1;

        for (let i = 0; i < counter.length; i++) {
            sum += counter[i];
            modulo += code[i];
        }
        if (sum < modulo) {
            return Number.MAX_VALUE;
        }

        barWidth = sum / modulo;
        maxSingleError *= barWidth;
        for (let i = 0; i < counter.length; i++) {
            count = counter[i];
            scaled = code[i] * barWidth;
            singleError = Math.abs(count - scaled) / scaled;
            if (singleError > maxSingleError) {
                return Number.MAX_VALUE;
            }
            error += singleError;
        }
        return error / modulo;
    }

    _nextSet(line: ReadonlyArray<number>, offset: number = 0) {
        for (let i = offset; i < line.length; i++) {
            if (line[i]) return i;
        }
        return line.length;
    }

    _correctBars(counter: Array<number>, correction: number, indices: Array<number>) {
        let length = indices.length;
        let tmp = 0;
        while (length--) {
            tmp = counter[indices[length]] * (1 - (1 - correction) / 2);
            if (tmp > 1) {
                counter[indices[length]] = tmp;
            }
        }
    }

    decodePattern(pattern: Array<number>) {
        // console.warn('* decodePattern', pattern);
        this._row = pattern;
        // console.warn('* decodePattern calling decode', typeof this, this.constructor, this.FORMAT, JSON.stringify(this));
        let result = this._decode();
        // console.warn('* first result=', result);
        if (result === null) {
            this._row.reverse();
            result = this._decode();
            // console.warn('* reversed result=', result);
            if (result) {
                result.direction = BarcodeDirection.Reverse;
                result.start = this._row.length - result.start;
                result.end = this._row.length - result.end;
            }
        } else {
            result.direction = BarcodeDirection.Forward;
        }
        // console.warn('* returning', result);
        return result;
    }

    _matchRange(start: number, end: number, value: number) {
        start = start < 0 ? 0 : start;
        for (let i = start; i < end; i++) {
            if (this._row[i] !== value) {
                return false;
            }
        }
        return true;
    }

    _fillCounters(
        offset: number = this._nextUnset(this._row),
        end: number = this._row.length,
        isWhite: boolean = true
    ) {
        const counters: Array<number> = [];
        let counterPos = 0;
        counters[counterPos] = 0;
        for (let i = offset; i < end; i++) {
            if (this._row[i] ^ (isWhite ? 1 : 0)) {
                counters[counterPos]++;
            } else {
                counterPos++;
                counters[counterPos] = 1;
                isWhite = !isWhite;
            }
        }
        return counters;
    }

    _toCounters(start: number, counters: Uint16Array | Array<number>) {
        const numCounters = counters.length;
        const end = this._row.length;
        let isWhite = !this._row[start];
        let counterPos = 0;

        for (let i = 0; i < counters.length; i++) {
            counters[i] = 0;
        }

        for (let i = start; i < end; i++) {
            if (this._row[i] ^ (isWhite ? 1 : 0)) {
                counters[counterPos]++;
            } else {
                counterPos++;
                if (counterPos === numCounters) {
                    break;
                } else {
                    counters[counterPos] = 1;
                    isWhite = !isWhite;
                }
            }
        }
        return counters;
    }
}
